import React, { useEffect, useState } from 'react';
import { Button, Col, Row } from 'reactstrap';
import InvoicingCycleRunConfirm from './InvoicingCycleRunConfirm';
import InvoicingCycleRunResult from './InvoicingCycleRunResult';
import {
  fetchAvailability,
  fetchRun,
  isTerminalStatus,
  requestFailed,
  triggerRun,
} from '../invoicingCycleApi';

const POLL_INTERVAL_MS = 3000;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const UNKNOWN_ENVIRONMENT = { available: false, environment: 'unknown' };

const pause = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));

/** Whether the chosen month has not happened yet; the cycle services cannot express one. */
const isFuturePeriod = (year, month, today) =>
  year > today.getFullYear() || (year === today.getFullYear() && month > today.getMonth() + 1);

/** Resolves once, on mount; a refused environment and an unreachable API are both "no". */
const useAvailability = () => {
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    fetchAvailability()
      .then(response => setAvailability(requestFailed(response) ? UNKNOWN_ENVIRONMENT : response.data))
      .catch(() => setAvailability(UNKNOWN_ENVIRONMENT));
  }, []);

  return availability;
};

const PeriodSelect = ({ label, value, options, onChange }) => (
  <Col md={3}>
    <span>{label}</span>
    <select className='form-control' value={value} onChange={event => onChange(Number(event.target.value))}>
      {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
    </select>
  </Col>
);

const InvoicingCycleRunForm = () => {
  const today = new Date();

  const availability = useAvailability();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isResultOpen, setResultOpen] = useState(false);
  const [run, setRun] = useState(null);
  const [isRunning, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const toggleConfirm = () => setConfirmOpen(prev => !prev);
  const toggleResult = () => setResultOpen(prev => !prev);

  /**
   * The cycle outlives the request that started it, so the run record is the only place its
   * outcome exists. Poll it rather than holding a connection open for minutes - a synchronous
   * run was measured timing out at 300 seconds while the billing carried on regardless.
   */
  const pollUntilFinished = async runId => {
    for (;;) {
      await pause(POLL_INTERVAL_MS);

      const response = await fetchRun(runId);

      if (requestFailed(response)) {
        setError('Lost track of the run. It is still going on the server - reload to check on it.');
        return;
      }

      setRun(response.data);

      if (isTerminalStatus(response.data?.status)) {
        return;
      }
    }
  };

  const startRun = async () => {
    const response = await triggerRun(year, month);

    if (requestFailed(response)) {
      setError(response?.data?.message || `The run could not be started (status ${response?.status}).`);
      return;
    }

    setRun(response.data);
    setResultOpen(true);

    await pollUntilFinished(response.data.id);
  };

  const onConfirmRun = async () => {
    setRunning(true);
    setError(null);

    try {
      await startRun();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message);
    }

    setConfirmOpen(false);
    setRunning(false);
  };

  if (availability === null) {
    return <p>Loading...</p>;
  }

  if (!availability.available) {
    return (
      <p>
        Running an invoicing cycle on demand is not available in the
        <strong> {availability.environment} </strong> environment. It creates real invoices, so it
        is limited to test environments.
      </p>
    );
  }

  const monthOptions = MONTHS.map((name, index) => ({ value: index + 1, label: name }));
  const yearOptions = [today.getFullYear(), today.getFullYear() - 1]
    .map(value => ({ value, label: value }));
  const periodIsFuture = isFuturePeriod(year, month, today);

  return (
    <>
      <p style={{ color: '#a5a5a5' }}>
        Runs the pay-in-arrears and advance-pay cycles for a chosen month, in that order, without
        waiting for the calendar. Environment: <strong>{availability.environment}</strong>.
      </p>

      <Row className='mt-3'>
        <PeriodSelect label='Month' value={month} options={monthOptions} onChange={setMonth} />
        <PeriodSelect label='Year' value={year} options={yearOptions} onChange={setYear} />
        <Col md={3} className='d-flex align-items-end'>
          <Button color='primary' disabled={periodIsFuture || isRunning} onClick={toggleConfirm}>
            {isRunning ? 'Running...' : 'Run Invoicing Cycle'}
          </Button>
        </Col>
      </Row>

      {periodIsFuture && (
        <p className='mt-3' style={{ color: '#c88' }}>
          A billing period in the future cannot be invoiced.
        </p>
      )}

      {error && <p className='mt-3' style={{ color: '#c88' }}>{error}</p>}

      <InvoicingCycleRunConfirm
        action={onConfirmRun}
        period={`${MONTHS[month - 1]} ${year}`}
        isRunning={isRunning}
        isOpen={isConfirmOpen}
        toggle={toggleConfirm}
      />
      <InvoicingCycleRunResult run={run} isOpen={isResultOpen} toggle={toggleResult} />
    </>
  )
}

export default InvoicingCycleRunForm;
